<template>
  <WelcomeHeader />
  <GuidelineNav current="english" />

  <!-- ═══ HERO ═══ -->
  <section class="hero-section">
    <div class="hero-noise"></div>
    <div class="container hero-inner">
      <div class="hero-badge">বিসিএস প্রস্তুতি গাইড · সংস্করণ ৪.০</div>
      <h1 class="hero-title">বিসিএস ইংরেজি <span class="hero-accent">গাইডলাইন</span></h1>
      <p class="hero-sub">বিসিএস ৩৫ থেকে ৫০ পর্যন্ত সম্পূর্ণ টপিকভিত্তিক প্রশ্ন বিশ্লেষণ</p>
      <div class="hero-stats">
        <div class="hstat"><div class="hstat-num">৩০</div><div class="hstat-lbl">মোট মার্ক</div></div>
        <div class="hstat-sep"></div>
        <div class="hstat"><div class="hstat-num">১৫</div><div class="hstat-lbl">English Language</div></div>
        <div class="hstat-sep"></div>
        <div class="hstat"><div class="hstat-num">১৫</div><div class="hstat-lbl">English Literature</div></div>
        <div class="hstat-sep"></div>
        <div class="hstat"><div class="hstat-num">১৬টি</div><div class="hstat-lbl">বিসিএস বিশ্লেষণ</div></div>
        <div class="hstat-sep"></div>
        <div class="hstat"><div class="hstat-num">৯ লাখ+</div><div class="hstat-lbl">অ্যাপ ডাউনলোড</div></div>
      </div>
    </div>
  </section>

  <!-- ═══ LEGEND BAR ═══ -->
  <div class="legend-bar">
    <div class="legend-inner">
      <span class="legend-label">হিটম্যাপ স্কেল:</span>
      <span class="lc h0">০</span>
      <span class="lc h1">১</span>
      <span class="lc h2">২</span>
      <span class="lc h3">৩+</span>
      <span class="legend-sep">|</span>
      <span class="pri-tag p3">★★★ অবশ্যই</span>
      <span class="pri-tag p2">★★ গুরুত্বপূর্ণ</span>
      <span class="pri-tag p1">★ এক নজর</span>
    </div>
  </div>

  <!-- ═══ TABS ═══ -->
  <section class="main-section">
    <div class="container">
      <div class="tab-row">
        <button class="tab-btn" :class="{ active: tab === 'language' }" @click="tab = 'language'">
          <span class="tab-icon">📚</span>
          <span>English Language</span>
          <span class="tab-mark">মার্ক = ১৫</span>
        </button>
        <button class="tab-btn" :class="{ active: tab === 'literature' }" @click="tab = 'literature'">
          <span class="tab-icon">📖</span>
          <span>English Literature</span>
          <span class="tab-mark">মার্ক = ১৫</span>
        </button>
      </div>

      <!-- ── LANGUAGE TAB ── -->
      <div v-if="tab === 'language'" class="tab-content">
        <div class="chart-card">
          <h3 class="chart-title">Top Topics — Total Questions (BCS 35–50)</h3>
          <div class="bar-list">
            <div v-for="row in langChart" :key="row.topic" class="bar-row">
              <div class="bar-label">{{ row.topic }}</div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: (row.total / 32 * 100) + '%' }" :class="'bp' + row.priority"></div>
              </div>
              <div class="bar-val">{{ row.total }}</div>
            </div>
          </div>
        </div>

        <div class="section-head">
          <h2>টপিকভিত্তিক প্রশ্ন হিটম্যাপ</h2>
          <p>বিসিএস ৩৫ থেকে ৫০ পর্যন্ত প্রতিটি বিসিএসে কতটি প্রশ্ন এসেছে</p>
        </div>

        <div class="table-wrap">
          <table class="heat-table">
            <thead>
              <tr>
                <th class="t-topic">Topic</th>
                <th>৩৫</th><th>৩৬</th><th>৩৭</th><th>৩৮</th><th>৩৯</th>
                <th>৪০</th><th>৪১</th><th>৪২</th><th>৪৩</th><th>৪৪</th><th>৪৫</th>
                <th class="t-new">৪৬</th><th class="t-new">৪৭</th><th class="t-new">৫০</th>
                <th class="t-total">Total</th>
                <th class="t-pri">Priority</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in languageData" :key="row.topic">
                <td class="t-name">{{ row.topic }}</td>
                <td v-for="(val, i) in row.values" :key="i" :class="heatClass(val)">
                  {{ val > 0 ? val : '' }}
                </td>
                <td class="t-total-cell">{{ row.total }}</td>
                <td class="t-pri-cell"><span :class="'pb p' + row.priority">{{ priorityLabel(row.priority) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="insight-grid">
          <div class="ins-card must">
            <div class="ins-head">★★★ Must Study</div>
            <ul>
              <li>Prepositions</li>
              <li>Idioms &amp; Phrases</li>
              <li>Synonyms &amp; Antonyms</li>
              <li>Meaning / Vocabulary</li>
              <li>Right Forms of Verb</li>
              <li>Corrections</li>
              <li>Verb &amp; Modals</li>
            </ul>
          </div>
          <div class="ins-card imp">
            <div class="ins-head">★★ Important</div>
            <ul>
              <li>Voice (Active/Passive)</li>
              <li>Spellings</li>
              <li>Noun / Gender / Number</li>
              <li>Subject-Verb Agreement</li>
              <li>Clauses</li>
              <li>Transformation</li>
            </ul>
          </div>
          <div class="ins-card low">
            <div class="ins-head">★ Quick Review</div>
            <ul>
              <li>Determiners / Article</li>
              <li>Adjective</li>
              <li>Narration</li>
              <li>Adverb</li>
              <li>Pronoun</li>
              <li>Prefix &amp; Suffix</li>
            </ul>
          </div>
        </div>

        <div class="analysis-box">
          <h4>📊 বিশ্লেষণ</h4>
          <p>English Language-এ <strong>Prepositions, Idioms &amp; Phrases, Synonyms &amp; Antonyms, Meaning/Vocabulary, Right Forms of Verb, Corrections, Verb &amp; Modals</strong> — এগুলো থেকে প্রতি বছরই প্রশ্ন আসে। তাই এই টপিকগুলো গভীরে গিয়ে পড়তে হবে।</p>
          <p class="mb-0">English Language-এ কিছু টপিক থেকে প্রশ্ন হয় না বললেই চলে — যেমন <strong>Adjective, Gender of Noun, Degree, Adverb, Conjunction, Modal Auxiliaries, Proverbs, Group Verbs, Prefix Suffix, Analogy, Composition</strong> ইত্যাদি।</p>
        </div>
      </div>

      <!-- ── LITERATURE TAB ── -->
      <div v-if="tab === 'literature'" class="tab-content">
        <div class="chart-card">
          <h3 class="chart-title">Top Topics — Total Questions (BCS 35–50)</h3>
          <div class="bar-list">
            <div v-for="row in litChart" :key="row.topic" class="bar-row">
              <div class="bar-label">{{ row.topic }}</div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: (row.total / 41 * 100) + '%' }" :class="'bp' + row.priority"></div>
              </div>
              <div class="bar-val">{{ row.total }}</div>
            </div>
          </div>
        </div>

        <div class="section-head">
          <h2>টপিকভিত্তিক প্রশ্ন হিটম্যাপ</h2>
          <p>বিসিএস ৩৫ থেকে ৫০ পর্যন্ত প্রতিটি বিসিএসে কতটি প্রশ্ন এসেছে</p>
        </div>

        <div class="table-wrap">
          <table class="heat-table">
            <thead>
              <tr>
                <th class="t-topic">Topic</th>
                <th>৩৫</th><th>৩৬</th><th>৩৭</th><th>৩৮</th><th>৩৯</th>
                <th>৪০</th><th>৪১</th><th>৪২</th><th>৪৩</th><th>৪৪</th><th>৪৫</th>
                <th class="t-new">৪৬</th><th class="t-new">৪৭</th><th class="t-new">৫০</th>
                <th class="t-total">Total</th>
                <th class="t-pri">Priority</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in literatureData" :key="row.topic">
                <td class="t-name">{{ row.topic }}</td>
                <td v-for="(val, i) in row.values" :key="i" :class="heatClass(val)">
                  {{ val > 0 ? val : '' }}
                </td>
                <td class="t-total-cell">{{ row.total }}</td>
                <td class="t-pri-cell"><span :class="'pb p' + row.priority">{{ priorityLabel(row.priority) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="insight-grid">
          <div class="ins-card must">
            <div class="ins-head">★★★ Must Study</div>
            <ul>
              <li>Modern Period (Edwardian/Georgian)</li>
              <li>Elizabethan Period</li>
              <li>Romantic Period</li>
              <li>Victorian Period</li>
            </ul>
          </div>
          <div class="ins-card imp">
            <div class="ins-head">★★ Important</div>
            <ul>
              <li>Neo-classical / Augustan Period</li>
              <li>Literary Terms</li>
              <li>Jacobean Period</li>
              <li>Quotations &amp; Characters</li>
            </ul>
          </div>
          <div class="ins-card low">
            <div class="ins-head">★ Quick Review</div>
            <ul>
              <li>Restoration Period</li>
              <li>Commonwealth / Puritan</li>
              <li>Age of Sensibility</li>
              <li>Postmodern Period</li>
            </ul>
          </div>
        </div>

        <div class="analysis-box">
          <h4>📊 বিশ্লেষণ</h4>
          <p>English Literature-এ <strong>The Elizabethan Period (William Shakespeare, Christopher Marlowe), The Romantic Period (William Wordsworth, S.T. Coleridge), The Victorian Period (Robert Browning), George Bernard Shaw, W.B. Yeats, The Modern Period, Literary Terms, Quotation</strong> — এগুলো থেকে প্রতি বছরই এক বা একাধিক প্রশ্ন আসে। ৪৭তম বিসিএসে Modern Period থেকে একাই ৬টি প্রশ্ন এসেছে।</p>
          <p class="mb-0"><strong>Old English Period, Middle English Period, The Caroline Period, The Commonwealth Period, The Age of Sensibility (William Blake), The Post Modern Period</strong> — এগুলো থেকে প্রশ্ন হয় না বললেই চলে।</p>
        </div>

        <!-- Important Writers -->
        <div class="authors-section">
          <div class="section-head">
            <h2>গুরুত্বপূর্ণ লেখক ও সাহিত্যিক</h2>
            <p>এই লেখকদের সম্পর্কে মূল তথ্য ও বিখ্যাত রচনা পড়লে Literature অংশ ভালো হবে</p>
          </div>
          <div class="author-groups">
            <div class="author-group">
              <div class="ag-header ag-psc">
                <span class="ag-badge">★★★★★ Most Important</span>
                <span class="ag-title">সর্বোচ্চ গুরুত্বপূর্ণ লেখক</span>
                <span class="ag-note">প্রায় প্রতিটি বিসিএসে প্রশ্ন আসে</span>
              </div>
              <div class="author-grid">
                <div class="author-card ac-psc" v-for="a in topAuthors" :key="a">
                  <span class="ac-dot psc-dot"></span>{{ a }}
                </div>
              </div>
            </div>
            <div class="author-group">
              <div class="ag-header ag-pancha">
                <span class="ag-badge">★★★ Important</span>
                <span class="ag-title">গুরুত্বপূর্ণ লেখক</span>
                <span class="ag-note">মূল তথ্য ও বিখ্যাত রচনা পড়তে হবে</span>
              </div>
              <div class="author-grid">
                <div class="author-card ac-pancha" v-for="a in midAuthors" :key="a">
                  <span class="ac-dot pancha-dot"></span>{{ a }}
                </div>
              </div>
            </div>
            <div class="author-group">
              <div class="ag-header ag-extra">
                <span class="ag-badge">★ Quick Review</span>
                <span class="ag-title">অন্যান্য উল্লেখযোগ্য লেখক</span>
                <span class="ag-note">এঁদের নাম ও প্রধান রচনাটি জানলেই হবে</span>
              </div>
              <div class="author-grid">
                <div class="author-card ac-extra" v-for="a in lowAuthors" :key="a">
                  <span class="ac-dot extra-dot"></span>{{ a }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ FREE PDF OPT-IN ═══ -->
  <GuidelineOptin subject="english" pdf="https://www.bcsprostuti.com/api5/storage/pdfs/BCS-English-Guideline-3_2026_06_20_14_23_34.pdf" />

  <!-- ═══ REFERENCE BOOKS ═══ -->
  <section class="ref-section">
    <div class="container">
      <div class="section-head center">
        <h2>Reference Books</h2>
        <p>পরীক্ষায় ভালো করার জন্য এই বইগুলো অনুসরণ করুন</p>
      </div>
      <div class="book-grid">
        <div class="book-card" v-for="b in refBooks" :key="b.title">
          <div class="book-spine"></div>
          <div class="book-info">
            <div class="book-title">{{ b.title }}</div>
            <div class="book-author">{{ b.author }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ TIPS ═══ -->
  <section class="tips-section">
    <div class="container">
      <div class="section-head center">
        <h2>পরামর্শ ও কৌশল</h2>
        <p>কীভাবে ইংরেজিতে সর্বোচ্চ নম্বর তুলবেন</p>
      </div>
      <div class="tips-grid">
        <div class="tip-card" v-for="(tip, i) in tips" :key="i">
          <div class="tip-num">{{ i + 1 }}</div>
          <p class="tip-text">{{ tip }}</p>
        </div>
      </div>
    </div>
  </section>

  <GuidelineRelated current="english" />

  <!-- ═══ CTA ═══ -->
  <section class="cta-section">
    <div class="container">
      <div class="cta-grid">
        <div class="cta-left">
          <div class="cta-tag">বিসিএস প্রস্তুতি অ্যাপ</div>
          <h2 class="cta-title">ইংরেজিতে সর্বোচ্চ নম্বর তুলুন স্মার্ট প্রস্তুতিতে</h2>
          <ul class="cta-features">
            <li><span class="cf-icon">✓</span> টপিকভিত্তিক প্রশ্ন ব্যাঙ্ক ও ব্যাখ্যা</li>
            <li><span class="cf-icon">✓</span> বিগত ৩৫–৫০ বিসিএসের সম্পূর্ণ প্রশ্নসমূহ</li>
            <li><span class="cf-icon">✓</span> স্মার্ট প্রগ্রেস ট্র্যাকিং সিস্টেম</li>
            <li><span class="cf-icon">✓</span> লাইভ কোর্স ও মডেল টেস্ট</li>
          </ul>
          <div class="cta-actions">
            <a href="https://play.google.com/store/apps/details?id=com.bcsprostuti.tanim.bcsprostuti" target="_blank" class="cta-play" @click="trackAppInstall">
              <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" height="48">
            </a>
            <a href="/packages" class="cta-pkg" @click="trackInitiateCheckout">প্যাকেজ দেখুন →</a>
          </div>
          <p class="cta-count">৯ লাখ+ শিক্ষার্থীর বিশ্বাসের অ্যাপ</p>
        </div>
        <div class="cta-right">
          <div class="cta-stat-card"><div class="cs-num">৯ লাখ+</div><div class="cs-lbl">অ্যাপ ডাউনলোড</div></div>
          <div class="cta-stat-card"><div class="cs-num">৩৫–৫০</div><div class="cs-lbl">বিসিএস বিশ্লেষণ</div></div>
          <div class="cta-stat-card"><div class="cs-num">১০০%</div><div class="cs-lbl">টপিক কভারেজ</div></div>
          <div class="cta-stat-card"><div class="cs-num">৩৫+</div><div class="cs-lbl">গুরুত্বপূর্ণ লেখক</div></div>
        </div>
      </div>
    </div>
  </section>

  <WelcomeFooter :hide-download="true" />

  <MessengerFloat subject="english" />
</template>

<script>
import WelcomeHeader from "@/components/WelcomePages/WelcomeHeader.vue";
import WelcomeFooter from "@/components/WelcomePages/WelcomeFooter.vue";
import GuidelineNav from "@/components/WelcomePages/GuidelineNav.vue";
import GuidelineRelated from "@/components/WelcomePages/GuidelineRelated.vue";
import GuidelineOptin from "@/components/WelcomePages/GuidelineOptin.vue";
import MessengerFloat from "@/components/WelcomePages/MessengerFloat.vue";
import * as pixels from "@/config/pixels";
import { ref, computed, onMounted } from 'vue';

export default {
  components: { WelcomeHeader, WelcomeFooter, GuidelineNav, GuidelineRelated, GuidelineOptin, MessengerFloat },
  setup() {
    const tab = ref('language');

    // BCS: 35,36,37,38,39,40,41,42,43,44,45,46,47,50
    const languageData = [
      { topic: 'Prepositions',           values: [1,5,1,2,1,2,3,1,2,2,4,4,2,2], total: 32, priority: 3 },
      { topic: 'Synonyms & Antonyms',    values: [5,3,4,2,1,3,0,0,2,2,2,2,2,2], total: 30, priority: 3 },
      { topic: 'Idioms & Phrases',       values: [1,1,4,3,1,2,1,2,3,2,2,2,1,2], total: 27, priority: 3 },
      { topic: 'Meaning / Vocabulary',   values: [6,1,2,0,8,3,0,1,2,1,2,1,0,0], total: 27, priority: 3 },
      { topic: 'Verb & Modals',          values: [0,0,1,3,1,1,3,1,0,0,0,2,3,2], total: 17, priority: 3 },
      { topic: 'Noun / Gender / Number', values: [0,0,1,2,1,2,2,2,2,1,0,0,2,2], total: 17, priority: 2 },
      { topic: 'Right Forms of Verb',    values: [1,1,1,1,0,0,0,3,1,1,2,3,0,0], total: 14, priority: 3 },
      { topic: 'Spellings',              values: [0,0,0,1,3,1,1,2,1,2,1,0,1,1], total: 14, priority: 2 },
      { topic: 'Corrections',            values: [1,2,1,1,0,0,0,0,1,0,2,4,1,0], total: 13, priority: 3 },
      { topic: 'Voice',                  values: [1,0,1,1,1,1,1,0,1,2,2,0,1,1], total: 13, priority: 2 },
      { topic: 'Subject-Verb Agreement', values: [0,1,0,0,0,0,1,0,1,0,3,2,0,0], total:  8, priority: 2 },
      { topic: 'Clauses',                values: [0,0,1,1,0,0,1,0,0,1,1,1,1,1], total:  8, priority: 2 },
      { topic: 'Determiners / Article',  values: [2,0,1,1,1,1,0,1,0,0,0,0,1,0], total:  8, priority: 1 },
      { topic: 'Transformation',         values: [0,0,1,1,0,0,0,0,0,1,0,2,1,0], total:  6, priority: 2 },
      { topic: 'Adjective',              values: [2,0,0,0,0,0,0,1,1,1,0,0,1,0], total:  6, priority: 1 },
      { topic: 'Adverb',                 values: [0,0,1,1,0,0,1,0,0,0,0,0,1,0], total:  4, priority: 1 },
      { topic: 'Narration',              values: [0,0,0,1,0,0,0,1,0,0,0,0,0,1], total:  3, priority: 1 },
      { topic: 'Pronoun',                values: [0,0,0,1,0,0,1,0,0,0,1,0,0,0], total:  3, priority: 1 },
    ];

    const literatureData = [
      { topic: 'Modern Period',          values: [5,4,2,2,0,2,7,4,3,4,0,1,6,1], total: 41, priority: 3 },
      { topic: 'Elizabethan Period',     values: [3,3,3,2,1,4,4,0,2,2,1,3,2,2], total: 32, priority: 3 },
      { topic: 'Romantic Period',        values: [1,5,2,1,0,2,0,0,4,2,3,1,2,3], total: 26, priority: 3 },
      { topic: 'Victorian Period',       values: [2,3,1,0,1,5,3,0,2,1,3,1,2,2], total: 26, priority: 3 },
      { topic: 'Neo-classical / Augustan', values: [0,0,0,2,0,1,0,0,2,2,0,2,0,1], total: 10, priority: 2 },
      { topic: 'Literary Terms',         values: [1,1,4,2,0,0,0,0,0,0,0,1,0,0], total:  9, priority: 2 },
      { topic: 'Jacobean Period',        values: [0,0,1,2,0,1,0,0,0,2,0,0,0,0], total:  6, priority: 2 },
      { topic: 'Postmodern Period',      values: [0,0,0,0,0,0,1,0,1,0,1,1,0,1], total:  5, priority: 1 },
      { topic: 'Commonwealth / Puritan', values: [0,0,0,1,0,0,0,0,1,0,1,0,0,1], total:  4, priority: 1 },
      { topic: 'Restoration Period',     values: [0,0,1,0,0,0,0,0,0,1,0,0,1,0], total:  3, priority: 1 },
      { topic: 'Age of Sensibility',     values: [0,1,1,0,0,0,0,0,1,0,0,0,0,0], total:  3, priority: 1 },
      { topic: 'Metaphysical Period',    values: [0,0,0,0,0,0,0,0,0,0,0,0,0,1], total:  1, priority: 1 },
    ];

    const langChart = computed(() =>
      [...languageData].sort((a, b) => b.total - a.total).slice(0, 9)
    );
    const litChart = computed(() =>
      [...literatureData].sort((a, b) => b.total - a.total).slice(0, 8)
    );

    const topAuthors = [
      'William Shakespeare', 'John Milton', 'Alexander Pope',
      'William Wordsworth', 'S.T. Coleridge', 'Robert Browning',
      'George Bernard Shaw', 'W.B. Yeats', 'T.S. Eliot',
      'D.H. Lawrence', 'Bertrand Russell',
    ];

    const midAuthors = [
      'Charles Dickens', 'Ernest Hemingway', 'Francis Bacon',
      'John Donne', 'Jonathan Swift', 'Dr. Samuel Johnson',
      'Henry Fielding', 'George Eliot', 'Thomas Hardy',
      'Oscar Wilde', "O'Henry", 'Virginia Woolf',
      'George Orwell', 'James Joyce', 'William Somerset Maugham',
      'H.G. Wells', 'A.P.J. Abdul Kalam',
    ];

    const lowAuthors = [
      'Christopher Marlowe', 'Edmund Spenser', 'Ben Jonson',
      'Edmund Burke', 'Sir Walter Scott', 'Thomas Gray',
      'Rudyard Kipling', 'Daniel Defoe', 'John Keats',
      'Lord Byron', 'P.B. Shelley', 'Alfred Lord Tennyson',
    ];

    const refBooks = [
      { title: 'Cliffs TOEFL', author: 'Reference' },
      { title: 'English Grammar in Use', author: 'Raymond Murphy' },
      { title: 'Oxford Advanced Learner\'s Dictionary', author: 'Oxford' },
      { title: 'An ABC of English Literature', author: 'Dr. M. Mofizar Rahman' },
      { title: 'A Handbook on English Literature', author: 'Sharif Hossain Ahmad Chowdhury' },
      { title: 'English For Competitive Exam', author: 'MASTER by Jahangir Alam' },
    ];

    const tips = [
      'First read all previous BCS English questions (with explanations) from the BCS Prostuti app — this gives you a clear idea of what types of questions appear in the exam.',
      'Analyze the heatmap and focus deeply on ★★★ topics. Prepositions, Idioms & Phrases, Synonyms/Antonyms and Meaning appear every year.',
      'For Literature: William Shakespeare (1564-1616), The Romantic Period (1798-1832) poets, and The Modern Period — study these three very thoroughly to get 3-5 marks easily.',
      'Learn 20-25 Literary Terms (simile, metaphor, personification, hyperbole, satire, irony, alliteration, lyric, ballad, allegory etc.) — study in a group and revise repeatedly.',
      'Give special attention to Charles Dickens, George Bernard Shaw, T.S. Eliot, Ernest Hemingway — 1-2 marks may come directly from these 4 writers.',
      'Practice topic-wise tests from the BCS Prostuti app after studying each topic — identify your weak areas and revisit them before the exam.',
    ];

    // ── SEO helper ──
    const setMeta = (attrs) => {
      const selector = attrs.property
        ? `meta[property="${attrs.property}"]`
        : `meta[name="${attrs.name}"]`;
      let el = document.querySelector(selector);
      if (!el) { el = document.createElement('meta'); document.head.appendChild(el); }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };

    // ── Funnel events (centralized in @/config/pixels) ──
    const trackAppInstall = () => pixels.trackAppInstallClick('english');
    const trackInitiateCheckout = () => pixels.trackInitiateCheckout('english');

    const heatClass = (val) => {
      if (!val || val === 0) return 'h0';
      if (val === 1) return 'h1';
      if (val === 2) return 'h2';
      return 'h3';
    };
    const priorityLabel = (p) => ({ 3: '★★★', 2: '★★', 1: '★' }[p]);

    onMounted(() => {
      window.scrollTo(0, 0);
      document.title = 'BCS English Guideline — Topic-wise Analysis (35–50 BCS)';
      setMeta({ name: 'description', content: 'বিসিএস ৩৫ থেকে ৫০ পর্যন্ত English Language ও Literature-এর সম্পূর্ণ টপিকভিত্তিক প্রশ্ন বিশ্লেষণ। হিটম্যাপ, গুরুত্বপূর্ণ লেখক ও গাইডলাইন।' });
      setMeta({ name: 'keywords', content: 'BCS English Guideline, বিসিএস ইংরেজি, BCS English analysis, English Literature BCS, BCS preparation' });
      setMeta({ name: 'robots', content: 'index, follow' });
      setMeta({ property: 'og:title', content: 'BCS English Guideline — Topic-wise Analysis' });
      setMeta({ property: 'og:description', content: 'BCS 35–50 English Language & Literature complete heatmap analysis.' });
      setMeta({ property: 'og:url', content: 'https://www.bcsprostuti.com/english-guideline' });
      setMeta({ property: 'og:type', content: 'article' });
      setMeta({ property: 'og:image', content: 'https://www.bcsprostuti.com/og/english-guideline.jpg' });
      setMeta({ property: 'og:image:width', content: '1200' });
      setMeta({ property: 'og:image:height', content: '630' });
      setMeta({ property: 'og:image:alt', content: 'BCS English Guideline' });
      setMeta({ property: 'og:site_name', content: 'বিসিএস প্রস্তুতি' });
      setMeta({ name: 'twitter:card', content: 'summary_large_image' });
      setMeta({ name: 'twitter:title', content: 'BCS English Guideline — Topic-wise Analysis' });
      setMeta({ name: 'twitter:description', content: 'BCS 35–50 English Language & Literature complete heatmap analysis.' });
      setMeta({ name: 'twitter:image', content: 'https://www.bcsprostuti.com/og/english-guideline.jpg' });
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
      canonical.href = 'https://www.bcsprostuti.com/english-guideline';

      pixels.trackGuidelineView('english');
      pixels.armViewedGuideline50('english');

      const sd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'BCS English Guideline — Topic-wise Analysis (35–50 BCS)',
        description: 'BCS 35–50 English Language & Literature complete heatmap analysis with priority recommendations.',
        url: 'https://www.bcsprostuti.com/english-guideline',
        author: { '@type': 'Organization', name: 'বিসিএস প্রস্তুতি', url: 'https://www.bcsprostuti.com' },
        publisher: { '@type': 'Organization', name: 'বিসিএস প্রস্তুতি' },
        inLanguage: 'bn', dateModified: new Date().toISOString().split('T')[0],
      };
      const script = document.createElement('script');
      script.type = 'application/ld+json'; script.id = 'english-guideline-ld';
      script.textContent = JSON.stringify(sd);
      document.head.appendChild(script);
    });

    return {
      tab, languageData, literatureData, langChart, litChart,
      topAuthors, midAuthors, lowAuthors, refBooks, tips,
      heatClass, priorityLabel, trackAppInstall, trackInitiateCheckout,
    };
  }
};
</script>

<style scoped>
* { box-sizing: border-box; }

/* ══ HERO ══ */
.hero-section {
  position: relative;
  background: linear-gradient(140deg, #191B47 0%, #0d0f2e 55%, #1a0828 100%);
  padding: 80px 0 60px; overflow: hidden;
}
.hero-noise {
  position: absolute; inset: 0; pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
}
.hero-inner { position: relative; text-align: center; }
.hero-badge {
  display: inline-block; background: rgba(0,149,255,0.15);
  border: 1px solid rgba(0,149,255,0.4); color: #0095FF;
  font-size: 0.8rem; font-weight: 600; padding: 6px 18px;
  border-radius: 999px; letter-spacing: 0.05em; margin-bottom: 20px;
}
.hero-title {
  font-family: 'Noto Serif Bengali', serif;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 900; color: #fff; line-height: 1.25; margin-bottom: 16px;
}
.hero-accent {
  background: linear-gradient(90deg, #0095FF, #00c6ff);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
}
.hero-sub { font-size: 1.05rem; color: #94a3b8; margin-bottom: 40px; }
.hero-stats {
  display: flex; align-items: center; gap: 0;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 16px; padding: 16px 20px;
  backdrop-filter: blur(12px); flex-wrap: wrap; justify-content: center; row-gap: 12px;
}
.hstat { text-align: center; padding: 0 16px; }
.hstat-num { font-family: 'Noto Serif Bengali', serif; font-size: clamp(1.3rem,3vw,2rem); font-weight: 900; color: #0095FF; line-height: 1; }
.hstat-lbl { font-size: 0.72rem; color: #94a3b8; margin-top: 4px; }
.hstat-sep { width: 1px; height: 40px; background: rgba(255,255,255,0.15); flex-shrink: 0; }
@media(max-width:768px) { .hstat-sep { display: none; } .hstat { padding: 4px 12px; } }

/* ══ LEGEND ══ */
.legend-bar { background: #f8fafc; border-bottom: 1px solid #e2e8f0; padding: 10px 16px; overflow-x: auto; }
.legend-inner {
  display: flex; align-items: center; justify-content: flex-start; gap: 8px; font-size: 0.82rem; white-space: nowrap; min-width: max-content;
}
@media(min-width: 769px) {
  .legend-inner {
    justify-content: center;
    margin: 0 auto;
  }
}
.legend-label { font-weight: 700; color: #374151; margin-right: 4px; }
.lc { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 26px; border-radius: 6px; font-size: 0.78rem; font-weight: 700; border: 1px solid #e2e8f0; }
.h0 { background: #f1f5f9; color: #94a3b8; }
.h1 { background: #fef9c3; color: #854d0e; }
.h2 { background: #fed7aa; color: #7c2d12; }
.h3 { background: #fca5a5; color: #7f1d1d; font-weight: 800; }
.legend-sep { color: #cbd5e1; margin: 0 4px; }
.pri-tag { font-size: 0.76rem; font-weight: 700; padding: 3px 10px; border-radius: 999px; cursor: default; }
.pri-tag.p3 { background: #fef2f2; color: #991b1b; }
.pri-tag.p2 { background: #fffbeb; color: #92400e; }
.pri-tag.p1 { background: #f0fdf4; color: #166534; }

/* ══ MAIN ══ */
.main-section { padding: 40px 0; }
@media(max-width:480px) { .main-section { padding: 24px 0; } }

.tab-row { display: flex; gap: 12px; justify-content: center; margin-bottom: 36px; flex-wrap: wrap; }
.tab-btn {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  background: #f1f5f9; border: 2px solid #e2e8f0; border-radius: 14px;
  padding: 16px 28px; font-size: 0.95rem; font-weight: 700; color: #475569;
  cursor: pointer; transition: all 0.22s; flex: 1; min-width: 150px; max-width: 240px;
}
.tab-btn:hover { background: #e2e8f0; border-color: #94a3b8; }
.tab-btn.active { background: #191B47; border-color: #191B47; color: #fff; box-shadow: 0 6px 20px rgba(25,27,71,0.35); }
.tab-btn.active span { color: #fff !important; }
.tab-icon { font-size: 1.5rem; line-height: 1; }
.tab-mark { font-size: 0.7rem; font-weight: 700; background: rgba(255,255,255,0.2); color: #fff; padding: 2px 10px; border-radius: 999px; }
.tab-btn:not(.active) .tab-mark { background: rgba(25,27,71,0.1); color: #191B47; }
@media(max-width: 480px) {
  .tab-btn { padding: 10px 14px; min-width: 120px; font-size: 0.85rem; border-radius: 10px; }
  .tab-icon { font-size: 1.2rem; }
}
.tab-content {
  animation: fadeIn 0.2s ease;
  width: 100% !important;
  max-width: 100% !important;
  overflow: hidden !important;
}
@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

/* bar chart */
.chart-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 28px 32px; margin-bottom: 40px; box-shadow: 0 2px 12px rgba(0,0,0,0.05); overflow: hidden; }
.chart-title { font-size: 1rem; font-weight: 700; color: #191B47; margin-bottom: 24px; }
.bar-list { display: flex; flex-direction: column; gap: 10px; }
.bar-row { display: flex; align-items: center; gap: 10px; }
.bar-label { font-size: 0.78rem; font-weight: 600; color: #374151; width: 160px; flex-shrink: 0; text-align: right; }
.bar-track { flex: 1; height: 22px; background: #f1f5f9; border-radius: 6px; overflow: hidden; }
.bar-fill { height: 100%; border-radius: 6px; transition: width 0.8s cubic-bezier(0.4,0,0.2,1); }
.bp3 { background: linear-gradient(90deg, #DC2626, #f87171); }
.bp2 { background: linear-gradient(90deg, #D97706, #fbbf24); }
.bp1 { background: linear-gradient(90deg, #059669, #34d399); }
.bar-val { font-size: 0.85rem; font-weight: 700; color: #191B47; width: 28px; text-align: right; }
@media(max-width: 600px) {
  .bar-row { flex-wrap: wrap; gap: 6px 8px; }
  .bar-label { width: 100%; text-align: left; font-size: 0.8rem; font-weight: 700; flex-shrink: 0; }
  .bar-track { flex: 1; min-width: 0; }
  .bar-val { width: 28px; flex-shrink: 0; }
  .chart-card { padding: 16px 12px; }
}

/* section head */
.section-head { margin-bottom: 20px; }
.section-head h2 { font-size: 0.95rem; font-weight: 700; color: #191B47; margin: 0 0 3px 0; border-left: 3px solid #0095FF; display: block; padding: 2px 0 2px 10px; }
.section-head p { color: #64748b; font-size: 0.8rem; margin: 0; padding-left: 13px; }
.section-head.center { text-align: center; }
.section-head.center h2 { border-left: none; padding-left: 0; font-size: 1.1rem; border-bottom: 3px solid #0095FF; display: inline-block; padding-bottom: 4px; }
.section-head.center p { padding-left: 0; margin-top: 8px; }

/* heatmap table */
.table-wrap {
  width: 100% !important;
  max-width: 100% !important;
  display: block !important;
  overflow-x: scroll !important;
  overflow-y: hidden !important;
  -webkit-overflow-scrolling: touch !important;
  border-radius: 14px; box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  margin-bottom: 36px;
}
.table-wrap::-webkit-scrollbar {
  height: 6px;
  display: block;
}
.table-wrap::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.table-wrap::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}
.heat-table {
  width: 100% !important;
  max-width: none !important;
  min-width: 900px !important;
  border-collapse: collapse !important;
  font-size: 0.82rem; background: #fff;
}
.heat-table thead tr { background: #1e2a6e; }
.heat-table th { color: #e2e8f0; padding: 11px 7px; text-align: center; font-weight: 700; white-space: nowrap; font-size: 0.76rem; border-right: 1px solid rgba(255,255,255,0.1); }
.heat-table th.t-topic {
  text-align: left; padding-left: 16px; min-width: 165px; color: #fff;
  position: sticky; left: 0; background: #1e2a6e !important; z-index: 3;
  pointer-events: none;
  overflow: hidden; text-overflow: ellipsis; max-width: 165px;
}
@media(max-width: 768px) {
  .heat-table th.t-topic {
    min-width: 130px !important;
    max-width: 130px !important;
    padding-left: 8px;
  }
}
.heat-table th.t-new   { background: #0095FF; color: #1a1a1a; }
.heat-table th.t-total { background: #1d4ed8; color: #fff; min-width: 50px; }
.heat-table th.t-pri   { background: #6d28d9; color: #fff; min-width: 80px; }
.heat-table td { padding: 7px 6px; text-align: center; border-bottom: 1px solid #f1f5f9; font-size: 0.8rem; }
.heat-table tbody tr:hover td { filter: brightness(0.96); }
.heat-table td.t-name {
  text-align: left !important; padding-left: 14px; font-weight: 600; color: #1e293b; white-space: nowrap; font-size: 0.8rem;
  position: sticky; left: 0; background: #fff !important; z-index: 2;
  box-shadow: 2px 0 5px rgba(0,0,0,0.05);
  pointer-events: none;
  overflow: hidden; text-overflow: ellipsis; max-width: 165px;
}
@media(max-width: 768px) {
  .heat-table td.t-name {
    min-width: 130px !important;
    max-width: 130px !important;
    padding-left: 8px;
    font-size: 0.75rem;
  }
}
.t-total-cell { background: #eff6ff; color: #1e40af; font-weight: 700; }
.t-pri-cell   { background: #faf5ff; }
.pb { display: inline-block; padding: 3px 9px; border-radius: 999px; font-size: 0.72rem; font-weight: 800; }
.pb.p3 { background: #fef2f2; color: #991b1b; }
.pb.p2 { background: #fffbeb; color: #92400e; }
.pb.p1 { background: #f0fdf4; color: #166534; }

/* insight grid */
.insight-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; margin-bottom: 32px; }
@media(max-width:768px) { .insight-grid { grid-template-columns: 1fr; } }
.ins-card { border-radius: 14px; padding: 22px 20px; text-align: left; }
.ins-head { font-weight: 800; font-size: 0.9rem; margin-bottom: 14px; text-align: left; }
.ins-card ul { padding-left: 18px; margin: 0; text-align: left; }
.ins-card li { font-size: 0.88rem; margin-bottom: 7px; color: #374151; text-align: left; word-break: break-word; }
.ins-card.must { background: #fef2f2; border: 1px solid #fecaca; }
.ins-card.must .ins-head { color: #991b1b; }
.ins-card.imp  { background: #fffbeb; border: 1px solid #fde68a; }
.ins-card.imp  .ins-head { color: #92400e; }
.ins-card.low  { background: #f0fdf4; border: 1px solid #bbf7d0; }
.ins-card.low  .ins-head { color: #166534; }

/* analysis box */
.analysis-box { background: #f0f9ff; border-left: 4px solid #191B47; border-radius: 0 14px 14px 0; padding: 20px 24px; margin-bottom: 48px; text-align: left; }
.analysis-box h4 { font-size: 1rem; font-weight: 700; color: #191B47; margin-bottom: 10px; }
.analysis-box p { font-size: 0.9rem; color: #374151; margin-bottom: 10px; line-height: 1.7; }
.mb-0 { margin-bottom: 0; }

/* authors */
.authors-section { margin-top: 8px; }
.author-groups { display: flex; flex-direction: column; gap: 28px; }
.author-group { border-radius: 18px; overflow: hidden; border: 1px solid #e2e8f0; }
.ag-header { padding: 18px 24px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.ag-psc    { background: linear-gradient(90deg, #191B47, #312e81); }
.ag-pancha { background: linear-gradient(90deg, #065f46, #047857); }
.ag-extra  { background: linear-gradient(90deg, #7c2d12, #9a3412); }
.ag-badge  { font-size: 0.7rem; font-weight: 700; background: rgba(255,255,255,0.2); color: #fff; padding: 3px 10px; border-radius: 999px; }
.ag-title  { font-size: 1.05rem; font-weight: 800; color: #fff; }
.ag-note   { font-size: 0.75rem; color: rgba(255,255,255,0.65); margin-left: auto; text-align: right; }
@media(max-width:600px) { .ag-note { display: none; } .ag-header { padding: 14px 16px; } }
.author-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 8px; padding: 14px; background: #fafafa; text-align: left; }
.author-card { display: flex; align-items: center; gap: 10px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; font-size: 0.88rem; font-weight: 600; color: #1e293b; transition: box-shadow 0.15s; text-align: left; }
.author-card:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.1); }
.ac-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.psc-dot   { background: #4f46e5; }
.pancha-dot { background: #059669; }
.extra-dot  { background: #dc2626; }
@media(max-width:480px) {
  .author-grid { grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 6px; padding: 10px; }
  .author-card { font-size: 0.78rem; padding: 8px 8px; gap: 6px; }
}

/* ref books */
.ref-section { background: #f8fafc; padding: 48px 0; }
.book-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 12px; margin-top: 28px; width: 100%; }
@media(max-width:768px) { .book-grid { grid-template-columns: repeat(2,1fr); } }
@media(max-width:480px) { .book-grid { grid-template-columns: 1fr; } }
.book-card { display: flex; align-items: stretch; border-radius: 10px; overflow: hidden; border: 1px solid #e2e8f0; background: #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.04); transition: transform 0.18s, box-shadow 0.18s; }
.book-card:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,0,0,0.09); }
.book-spine { width: 5px; background: linear-gradient(180deg, #191B47, #312e81); flex-shrink: 0; }
.book-info { padding: 12px 14px; display: flex; flex-direction: column; justify-content: center; text-align: left; }
.book-title { font-size: 0.82rem; font-weight: 700; color: #1e293b; margin-bottom: 3px; line-height: 1.4; text-align: left; }
.book-author { font-size: 0.74rem; color: #64748b; text-align: left; }

/* tips */
.tips-section { background: #fff; padding: 48px 0; }
.tips-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; margin-top: 28px; }
@media(max-width:600px) { .tips-grid { grid-template-columns: 1fr; } }
.tip-card { display: flex; gap: 16px; align-items: flex-start; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 14px; padding: 20px; }
.tip-num { width: 32px; height: 32px; border-radius: 50%; background: #fff; color: #191B47; border: 2px solid #191B47; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 0.85rem; flex-shrink: 0; }
.tip-text { font-size: 0.9rem; color: #374151; line-height: 1.7; margin: 0; text-align: left; }

/* CTA */
.cta-section { background: #191B47; border-top: 4px solid #0095FF; padding: 64px 0; }
.cta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
@media(max-width:768px) { .cta-grid { grid-template-columns: 1fr; gap: 32px; } }
.cta-left { align-self: center; }
.cta-tag { display: inline-block; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #0095FF; background: rgba(0,149,255,0.15); border: 1px solid rgba(0,149,255,0.35); padding: 4px 14px; border-radius: 999px; margin-bottom: 14px; }
.cta-title { font-family: 'Noto Serif Bengali', serif; font-size: 1.5rem; font-weight: 800; color: #fff; line-height: 1.4; margin-bottom: 20px; }
.cta-features { list-style: none; padding: 0; margin: 0 0 24px 0; display: flex; flex-direction: column; gap: 9px; }
.cta-features li { display: flex; align-items: center; gap: 10px; font-size: 0.88rem; color: #c7d2fe; }
.cf-icon { width: 20px; height: 20px; background: rgba(0,149,255,0.2); color: #0095FF; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.7rem; font-weight: 800; flex-shrink: 0; }
.cta-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.cta-play { display: inline-flex; align-items: center; background: #fff; border-radius: 10px; padding: 7px 16px; text-decoration: none; transition: transform 0.2s; }
.cta-play:hover { transform: scale(1.03); }
.cta-pkg { display: inline-flex; align-items: center; background: #0095FF; color: #1a1a1a; font-weight: 700; font-size: 0.9rem; border-radius: 10px; padding: 11px 24px; text-decoration: none; transition: all 0.2s; }
.cta-pkg:hover { background: #007AD6; transform: scale(1.02); }
.cta-count { font-size: 0.78rem; color: #64748b; margin: 0; }
.cta-right { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; align-self: center; }
@media(max-width:768px) { .cta-right { grid-template-columns: repeat(2, 1fr); gap: 10px; } }
.cta-stat-card { background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.14); border-radius: 14px; padding: 24px 20px; text-align: center; }
.cs-num { font-family: 'Noto Serif Bengali', serif; font-size: 1.6rem; font-weight: 800; color: #0095FF; line-height: 1.1; }
.cs-lbl { font-size: 0.75rem; color: #94a3b8; margin-top: 7px; }
</style>
